import React, { useState, useEffect, useCallback } from 'react';
import { commonApi, orderAllocationsApi } from '../services/api';
import { useClientApi } from '@/hooks/useClientApi';
import { OrderAllocation } from '../types/client';
import shared from '@/components/utils/shared.module.css';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { FilterSelect, FilterAsyncSelect } from '@/components/ui';
import type { FilterOption } from '@/components/ui';

type SelectOption = FilterOption;

type SortField = 'trade_date' | 'client_master__client_code' | 'symbol__symbol' | 'quantity' | 'net_amt' | 'sell_buy';

const PAGE_SIZE = 20;

const SELL_BUY_OPTIONS: SelectOption[] = [
  { value: '1', label: 'Buy' },
  { value: '2', label: 'Sell' },
];

const OrderAllocationList: React.FC = () => {
  const { getClients, getClientFilter } = useClientApi();
  const [allocations, setAllocations] = useState<OrderAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [datesReady, setDatesReady] = useState(false);
  const [selectedClient, setSelectedClient] = useState<SelectOption | null>(null);
  const [selectedSellBuy, setSelectedSellBuy] = useState<SelectOption | null>(null);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [sortField, setSortField] = useState<SortField>('trade_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getClientFullName = (allocation: OrderAllocation): string => {
    const nameParts = [];
    if (allocation.client_first_name) nameParts.push(allocation.client_first_name);
    if (allocation.client_middle_name) nameParts.push(allocation.client_middle_name);
    if (allocation.client_last_name) nameParts.push(allocation.client_last_name);
    return nameParts.length > 0 ? nameParts.join(' ') : allocation.client_code;
  };

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const initialize = async () => {
      try {
        const data = await commonApi.getLastWorkingDay();
        setStartDate(data.last_working_day || today);
      } catch (err) {
        console.error('Error fetching last working day:', err);
        setStartDate(today);
      } finally {
        setEndDate(today);
        setDatesReady(true);
      }
    };
    initialize();
  }, []);

  const loadClientOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    try {
      const response = await getClients({ search: inputValue, page_size: 50 });
      return response.results.map(client => ({
        value: client.id.toString(),
        label: `${client.client_code} - ${client.full_name || `${client.first_name} ${client.last_name}`}`,
      }));
    } catch {
      return [];
    }
  }, [getClients]);

  const loadAllocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
      const response = await orderAllocationsApi.getOrderAllocations({
        page: currentPage,
        page_size: PAGE_SIZE,
        ...getClientFilter(clientVal),
        sell_buy: selectedSellBuy ? parseInt(selectedSellBuy.value) : undefined,
        trade_date__gte: startDate || undefined,
        trade_date__lte: endDate || undefined,
        search: debouncedSearchQuery.trim() || undefined,
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`,
      });
      setAllocations(response.results);
      setTotalCount(response.count);
      setTotalPages(Math.ceil(response.count / PAGE_SIZE));
    } catch (err: any) {
      console.error('Error loading order allocations:', err);
      setError(err.response?.data?.message || 'Failed to load order allocations');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedClient, selectedSellBuy, startDate, endDate, debouncedSearchQuery, sortField, sortDir, getClientFilter]);

  useEffect(() => {
    if (datesReady) loadAllocations();
  }, [loadAllocations, datesReady]);

  const formatNumber = (value: number | undefined, decimals = 2): string => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  };

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as SortField);
      setSortDir('asc');
    }
    setCurrentPage(1);
  };

  const hasActiveFilters = !!(selectedClient || selectedSellBuy || searchQuery.trim());

  const clearFilters = () => {
    setSelectedClient(null);
    setSelectedSellBuy(null);
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setCurrentPage(1);
  };

  const downloadCSV = async () => {
    setDownloadingCSV(true);
    try {
      const clientVal = selectedClient ? parseInt(selectedClient.value) : undefined;
      const response = await orderAllocationsApi.exportAllOrderAllocations({
        ...getClientFilter(clientVal),
        sell_buy: selectedSellBuy ? parseInt(selectedSellBuy.value) : undefined,
        trade_date__gte: startDate || undefined,
        trade_date__lte: endDate || undefined,
        search: debouncedSearchQuery.trim() || undefined,
        ordering: `${sortDir === 'desc' ? '-' : ''}${sortField}`,
      });

      const allAllocations = response.results;
      const headers = [
        'Trade Date', 'Client Code', 'Client Name', 'Symbol', 'Company',
        'Broker Code', 'Contract No', 'Buy/Sell', 'Quantity',
        'Mkt Rate', 'Brokerage', 'STT', 'Ser Tax', 'Sebi Tax',
        'Turn Tax', 'Stamp Duty', 'Exit Load', 'Net Rate', 'Net Amt'
      ];
      const rows = allAllocations.map(a => {
        const clientName = [a.client_first_name, a.client_middle_name, a.client_last_name]
          .filter(Boolean).join(' ') || a.client_code;
        return [
          a.trade_date, a.client_code, clientName, a.symbol_code, a.company_name,
          a.broker_code_display, a.contract_no_display, a.sell_buy_display,
          a.adj_quantity, a.adj_mkt_rate, a.brokerage, a.stt, a.ser_tax,
          a.sebi_tax, a.turn_tax, a.stamp_duty, a.exit_load ?? '', a.adj_net_rate, a.adj_net_amt
        ].map(val => `"${val}"`).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order_allocations_${startDate}_to_${endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading CSV:', err);
      setError(err.response?.data?.message || err.message || 'Failed to download CSV');
    } finally {
      setDownloadingCSV(false);
    }
  };

  const activeFilterCount = [selectedClient, selectedSellBuy].filter(Boolean).length;

  const columns: ColumnDef<OrderAllocation>[] = [
    { key: 'trade_date', header: 'Trade Date', sortable: true,
      render: r => formatDate(r.trade_date) },
    { key: 'client_master__client_code', header: 'Client Code', sortable: true,
      cellClassName: () => shared['cell-strong'], render: r => r.client_code },
    { key: 'client_name', header: 'Client Name', width: '200px',
      cellClassName: () => shared['col-wide'], render: r => getClientFullName(r) },
    { key: 'symbol__symbol', header: 'Symbol', sortable: true, render: r => r.symbol_code },
    { key: 'company_name', header: 'Company',
      cellClassName: () => shared['col-mid'], render: r => r.company_name },
    { key: 'broker', header: 'Broker', render: r => r.broker_code_display },
    { key: 'contract_no', header: 'Contract No', render: r => r.contract_no_display },
    { key: 'sell_buy', header: 'Buy/Sell', sortable: true,
      render: r => (
        <span className={r.sell_buy === 1 ? shared.profit : shared.loss}>
          {r.sell_buy_display}
        </span>
      ) },
    { key: 'quantity', header: 'Quantity', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_quantity, 0) },
    { key: 'adj_mkt_rate', header: 'Mkt Rate', align: 'right', render: r => formatNumber(r.adj_mkt_rate, 4) },
    { key: 'brokerage', header: 'Brokerage', align: 'right', render: r => formatNumber(r.brokerage, 4) },
    { key: 'stt', header: 'STT', tooltip: 'Securities Transaction Tax', align: 'right',
      render: r => formatNumber(r.stt, 4) },
    { key: 'ser_tax', header: 'Ser Tax', tooltip: 'Service Tax', align: 'right',
      render: r => formatNumber(r.ser_tax) },
    { key: 'sebi_tax', header: 'SEBI Tax', tooltip: 'SEBI Tax', align: 'right',
      render: r => formatNumber(r.sebi_tax) },
    { key: 'turn_tax', header: 'Turn Tax', tooltip: 'Turnover Tax', align: 'right',
      render: r => formatNumber(r.turn_tax) },
    { key: 'stamp_duty', header: 'Stamp Duty', align: 'right', render: r => formatNumber(r.stamp_duty) },
    { key: 'exit_load', header: 'Exit Load', align: 'right',
      render: r => (r.exit_load == null ? '-' : formatNumber(r.exit_load, 4)) },
    { key: 'adj_net_rate', header: 'Net Rate', align: 'right', render: r => formatNumber(r.adj_net_rate, 4) },
    { key: 'net_amt', header: 'Net Amt', sortable: true, align: 'right',
      render: r => formatNumber(r.adj_net_amt) },
  ];

  return (
    <DataTable<OrderAllocation>
      title="Order Allocations"
      subtitle="How each executed order was allocated across clients."
      data={allocations}
      columns={columns}
      rowKey={row => row.id}
      totalCount={totalCount}
      loading={loading}
      loadingMessage="Loading order allocations…"
      error={error}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={PAGE_SIZE}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={searchQuery}
      onSearchChange={value => { setSearchQuery(value); setCurrentPage(1); }}
      searchPlaceholder="Search by client, order no, symbol…"
      hasActiveFilters={hasActiveFilters}
      activeFilterCount={activeFilterCount}
      onClearFilters={clearFilters}
      onDownloadCSV={downloadCSV}
      isDownloading={downloadingCSV}
      emptyMessage="No order allocations found for the selected filters."
      filterSlot={
        <>
          <div className={shared['filter-group']}>
            <label htmlFor="start-date">From Date</label>
            <input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className={shared.input}
            />
          </div>

          <div className={shared['filter-group']}>
            <label htmlFor="end-date">To Date</label>
            <input
              id="end-date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className={shared.input}
            />
          </div>

          <FilterAsyncSelect
            label="Client"
            value={selectedClient}
            onChange={opt => { setSelectedClient(opt); setCurrentPage(1); }}
            loadOptions={loadClientOptions}
            placeholder="All Clients"
          />

          <FilterSelect
            label="Buy / Sell"
            value={selectedSellBuy}
            onChange={opt => { setSelectedSellBuy(opt); setCurrentPage(1); }}
            options={SELL_BUY_OPTIONS}
            placeholder="All"
          />
        </>
      }
    />
  );
};

export default OrderAllocationList;
